import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, EyeOff, History, Minus, Plus, Search, TimerReset, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Appointment, Daycare, Dog, MembershipPurchase, Payment, Price, Service, Stay } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input, Label, Select, Textarea } from '../../components/ui/field';
import { CustomerSearchBox } from '../../components/ui/search';
import { ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { api } from '../../shared/api';
import { formatCurrency, formatDate, formatDateTime } from '../../shared/format';
import { isOperationalRateService } from '../../shared/services';
import { buildPriceLookups, dogIdsForSource, extraDaycareHours, extraStayDays, latestUsablePrice, paymentStatus } from '../../shared/billing';
import {
  buildSourceOptions,
  customCharge,
  defaultPaymentDescription,
  canSubmitPayment,
  cartItemsForSource,
  cartPaymentSummary,
  cartForDirectPayment,
  centsFromAmountInput,
  createPaymentDraft,
  groupCartItems,
  isStoredCartItem,
  mergeServiceItemsForGroup,
  paidCentsForSuggestedItems,
  paymentStateForSource,
  priceServiceIdsForSource,
  quantityFromInput,
  serviceItemsForGroup,
  selectedPriceDateForSource,
  suggestedSelectionDefaults,
  suggestedItemsForSource,
  totalForSuggestedItems,
  type CartGroup,
  type CartItem,
  type SourceOption,
  type SourceType,
  type StoredCart,
  type SuggestedItem,
} from './payment-flow';

const CART_STORAGE_KEY = 'hotle:payments-cart:v1';
const CART_TTL_MS = 30 * 60 * 1000;

const sourceTypeLabels: Record<SourceType, string> = {
  daycare: 'Guardería',
  stay: 'Hotel',
  service: 'Servicio',
  membership: 'Membresia',
  other: 'Otro',
};

export function PaymentsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSourceType = searchParams.get('sourceType');
  const [initialShortcutParams] = useState(() => ({
    customerId: searchParams.get('customerId') ?? '',
    sourceType: initialSourceType === 'daycare' || initialSourceType === 'stay' || initialSourceType === 'service' || initialSourceType === 'membership' ? initialSourceType : '',
    sourceId: searchParams.get('sourceId') ?? '',
  }));
  const [customerId, setCustomerId] = useState(searchParams.get('customerId') ?? '');
  const [sourceType, setSourceType] = useState<SourceType | ''>(initialSourceType === 'daycare' || initialSourceType === 'stay' || initialSourceType === 'service' || initialSourceType === 'membership' ? initialSourceType : '');
  const [sourceId, setSourceId] = useState(searchParams.get('sourceId') ?? '');
  const [showChargeSearch, setShowChargeSearch] = useState(() => Boolean(searchParams.get('customerId') || searchParams.get('sourceType') || searchParams.get('sourceId') || searchParams.get('add') === '1'));
  const sourceLookupsEnabled = Boolean(customerId && sourceType && sourceType !== 'other');
  const servicesQuery = useQuery({ queryKey: ['services', 'payments'], queryFn: () => api.services.list({ limit: 100 }), enabled: sourceLookupsEnabled });
  const staysQuery = useQuery({ queryKey: ['stays', customerId], queryFn: () => api.stays.list({ limit: 100, customerId }), enabled: Boolean(customerId && sourceType === 'stay') });
  const daycaresQuery = useQuery({ queryKey: ['daycares', customerId], queryFn: () => api.daycares.list({ limit: 100, customerId }), enabled: Boolean(customerId && sourceType === 'daycare') });
  const membershipsQuery = useQuery({ queryKey: ['membership-purchases', customerId, 'payments'], queryFn: () => api.memberships.purchases({ limit: 100, customerId }), enabled: Boolean(customerId && sourceType === 'membership') });
  const linkedStayQuery = useQuery({ queryKey: ['stay', sourceId, 'payments'], queryFn: () => api.stays.get(sourceId), enabled: Boolean(sourceType === 'stay' && sourceId) });
  const linkedDaycareQuery = useQuery({ queryKey: ['daycare', sourceId, 'payments'], queryFn: () => api.daycares.get(sourceId), enabled: Boolean(sourceType === 'daycare' && sourceId) });
  const linkedAppointmentQuery = useQuery({ queryKey: ['appointment', sourceId, 'payments'], queryFn: () => api.appointments.get(sourceId), enabled: Boolean(sourceType === 'service' && sourceId.startsWith('appt_')) });
  const linkedServiceQuery = useQuery({ queryKey: ['service', sourceId, 'payments'], queryFn: () => api.services.get(sourceId), enabled: Boolean(sourceType === 'service' && sourceId && !sourceId.startsWith('appt_')) });
  const dogsQuery = useQuery({ queryKey: ['customer-dogs', customerId, 'payments'], queryFn: () => api.customers.allDogs(customerId), enabled: sourceLookupsEnabled });
  const paymentsQuery = useQuery({ queryKey: ['payments', 'checkout'], queryFn: () => api.payments.list({ limit: 100 }) });
  const appointmentsQuery = useQuery({ queryKey: ['appointments', customerId, 'payments'], queryFn: () => api.appointments.list({ limit: 100, customerId }), enabled: sourceLookupsEnabled });
  const services = servicesQuery.data?.items ?? [];
  const dogs = dogsQuery.data?.items ?? [];
  const selectedPriceDate = useMemo(() => selectedPriceDateForSource({
    sourceType,
    sourceId,
    stays: staysQuery.data?.items ?? [],
    daycares: daycaresQuery.data?.items ?? [],
    linkedStay: linkedStayQuery.data,
    linkedDaycare: linkedDaycareQuery.data,
    linkedAppointment: linkedAppointmentQuery.data,
    today: new Date().toISOString().slice(0, 10),
  }), [daycaresQuery.data?.items, linkedAppointmentQuery.data, linkedDaycareQuery.data, linkedStayQuery.data, sourceId, sourceType, staysQuery.data?.items]);
  const priceServiceIds = useMemo(() => priceServiceIdsForSource({
    sourceType,
    sourceId,
    stays: staysQuery.data?.items ?? [],
    daycares: daycaresQuery.data?.items ?? [],
    linkedStay: linkedStayQuery.data,
    linkedDaycare: linkedDaycareQuery.data,
    linkedAppointment: linkedAppointmentQuery.data,
  }), [daycaresQuery.data?.items, linkedAppointmentQuery.data, linkedDaycareQuery.data, linkedStayQuery.data, sourceId, sourceType, staysQuery.data?.items]);
  const pricesQuery = useQuery({
    queryKey: ['service-prices', 'payments', selectedPriceDate, priceServiceIds.join('|')],
    queryFn: async () => {
      const byId: Record<string, Price> = {};
      for (const serviceId of priceServiceIds) {
        try {
          const prices = await api.services.prices(serviceId, { limit: 100 });
          const price = latestUsablePrice(prices.items, selectedPriceDate);
          if (price) byId[serviceId] = price;
        } catch (error) {
          console.warn('Unable to load service prices for payments.', { serviceId, error });
        }
      }
      return byId;
    },
    enabled: sourceLookupsEnabled && priceServiceIds.length > 0,
    retry: 1,
  });
  const pricesByServiceId = useMemo(() => {
    return { ...(pricesQuery.data ?? {}) };
  }, [pricesQuery.data]);
  const priceLookups = useMemo(() => buildPriceLookups(services, pricesByServiceId), [pricesByServiceId, services]);
  const [description, setDescription] = useState('');
  const [manualAmount, setManualAmount] = useState('0');
  const [paymentAmount, setPaymentAmount] = useState('0');
  const [quantity, setQuantity] = useState('1');
  const [method, setMethod] = useState<Payment['method']>('cash');
  const [notes, setNotes] = useState('');
  const storedCart = useMemo(() => readStoredCart(), []);
  const initialCart = useMemo(() => cartForDirectPayment(storedCart, initialShortcutParams.sourceId), [initialShortcutParams.sourceId, storedCart]);
  const [items, setItems] = useState<CartItem[]>(() => initialCart.items);
  const [cartExpiresAt, setCartExpiresAt] = useState(() => initialCart.expiresAt);
  const [cartTimeLeftMs, setCartTimeLeftMs] = useState(() => Math.max(0, initialCart.expiresAt - Date.now()));
  const cartInitialized = useRef(false);
  const [snackbar, setSnackbar] = useState('');

  const paymentSummary = useMemo(() => cartPaymentSummary(items, paymentsQuery.data?.items ?? []), [items, paymentsQuery.data?.items]);
  const billBuckets = paymentSummary.billBuckets;
  const totalCents = paymentSummary.totalCents;
  const paidInCartCents = paymentSummary.paidCents;
  const remainingCartCents = paymentSummary.remainingCents;
  const paymentAmountCents = useMemo(() => centsFromAmountInput(paymentAmount), [paymentAmount]);
  const cartGroups = useMemo(() => groupCartItems(items), [items]);
  const sourceOptions = useMemo(() => buildSourceOptions({
    sourceType,
    customerId,
    memberships: membershipsQuery.data?.items ?? [],
    services,
    stays: staysQuery.data?.items ?? [],
    daycares: daycaresQuery.data?.items ?? [],
    linkedAppointment: linkedAppointmentQuery.data,
    linkedService: linkedServiceQuery.data,
    linkedStay: linkedStayQuery.data,
    linkedDaycare: linkedDaycareQuery.data,
    dogs,
    pricesByServiceId,
    priceLookups,
    payments: paymentsQuery.data?.items ?? [],
    appointments: appointmentsQuery.data?.items ?? [],
    isOperationalRateService,
    formatCurrency,
    formatDate,
    formatDateTime,
  }), [appointmentsQuery.data?.items, customerId, daycaresQuery.data?.items, dogs, linkedAppointmentQuery.data, linkedDaycareQuery.data, linkedServiceQuery.data, linkedStayQuery.data, membershipsQuery.data?.items, paymentsQuery.data?.items, priceLookups, pricesByServiceId, services, sourceType, staysQuery.data?.items]);

  const selectedSource = sourceOptions.find((option) => option.id === sourceId);
  const selectedSuggestedItems = useMemo(
    () => selectedSource ? suggestedItemsForSource(selectedSource, services, dogs, pricesByServiceId, priceLookups, paymentsQuery.data?.items ?? [], appointmentsQuery.data?.items ?? []) : [],
    [appointmentsQuery.data?.items, dogs, paymentsQuery.data?.items, priceLookups, pricesByServiceId, selectedSource, services],
  );
  const selectedPaymentState = useMemo(
    () => selectedSource ? paymentStateForSource(selectedSource, selectedSuggestedItems, paymentsQuery.data?.items ?? []) : null,
    [paymentsQuery.data?.items, selectedSource, selectedSuggestedItems],
  );
  const autoFilledSelection = selectedSuggestedItems.length > 0;
  const paymentCustomerId = customerId || items.find((item) => item.customerId)?.customerId || '';

  useEffect(() => {
    const defaults = suggestedSelectionDefaults(selectedSuggestedItems);
    if (!defaults) return;
    setQuantity(defaults.quantity);
    setManualAmount(defaults.amount);
  }, [selectedSuggestedItems]);

  useEffect(() => {
    setPaymentAmount(String(remainingCartCents / 100));
  }, [remainingCartCents]);

  useEffect(() => {
    if (items.length === 0) {
      setCartExpiresAt(0);
      setCartTimeLeftMs(0);
      window.localStorage.removeItem(CART_STORAGE_KEY);
      cartInitialized.current = true;
      return;
    }

    if (!cartInitialized.current) {
      cartInitialized.current = true;
      return;
    }

    const nextExpiresAt = Date.now() + CART_TTL_MS;
    setCartExpiresAt(nextExpiresAt);
    setCartTimeLeftMs(CART_TTL_MS);
    writeStoredCart(items, nextExpiresAt);
  }, [items]);

  useEffect(() => {
    if (items.length === 0 || cartExpiresAt <= 0) return;
    writeStoredCart(items, cartExpiresAt);
  }, [cartExpiresAt, items]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      if (cartExpiresAt <= 0) {
        setCartTimeLeftMs(0);
        return;
      }

      const nextTimeLeftMs = Math.max(0, cartExpiresAt - Date.now());
      setCartTimeLeftMs(nextTimeLeftMs);
      if (nextTimeLeftMs === 0) {
        setItems([]);
        setPaymentAmount('0');
        showSnackbar(setSnackbar, 'El carrito recordado expiro y fue limpiado.');
      }
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [cartExpiresAt]);

  const registerPayment = useMutation({
    mutationFn: () => {
      const draft = createPaymentDraft({
        customerId: paymentCustomerId,
        items,
        payments: paymentsQuery.data?.items ?? [],
        paymentCents: paymentAmountCents,
        method,
        notes: notes || undefined,
      });
      return api.payments.create(draft);
    },
    onSuccess: async () => {
      setItems([]);
      setSourceId('');
      setManualAmount('0');
      setPaymentAmount('0');
      setQuantity('1');
      setNotes('');
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });

  const clearCart = () => {
    setItems([]);
    setPaymentAmount('0');
    setCartExpiresAt(0);
    setCartTimeLeftMs(0);
    window.localStorage.removeItem(CART_STORAGE_KEY);
  };

  const resetChargeFields = () => {
    setCustomerId('');
    setSourceType('');
    setDescription('');
    setSourceId('');
    setManualAmount('0');
    setQuantity('1');
  };

  const closeChargeSearch = () => {
    resetChargeFields();
    setShowChargeSearch(false);
  };

  const changeSource = (id: string) => {
    setSourceId(id);
    const source = sourceOptions.find((option) => option.id === id)?.source;
    if (source && 'customerId' in source) setCustomerId(source.customerId);
  };

  const addItem = () => {
    if (sourceType === 'other') {
      try {
        const item = customCharge({ customerId, amount: manualAmount, quantity, description }, () => crypto.randomUUID());
        if (items.some((existing) => existing.customerId && existing.customerId !== customerId)) throw new Error('El carrito solo puede contener cobros de un cliente.');
        setItems((current) => [...current, item]);
        resetChargeFields();
        setShowChargeSearch(false);
      } catch (error) {
        showSnackbar(setSnackbar, error instanceof Error ? error.message : 'No se pudo agregar el concepto.');
      }
      return;
    }
    if (!selectedSource) return;
    const sourceCustomerId = 'customerId' in selectedSource.source ? selectedSource.source.customerId : customerId;
    if (sourceCustomerId && !customerId) setCustomerId(sourceCustomerId);
    const amountCents = centsFromAmountInput(manualAmount);
    const itemQuantity = quantityFromInput(quantity);
    let itemsToAdd: CartItem[];
    try {
      itemsToAdd = cartItemsForSource({
        selectedSource,
        suggestedItems: selectedSuggestedItems,
        existingItems: items,
        sourceCustomerId,
        sourceTypeLabel: sourceTypeLabels[selectedSource.sourceType],
        manualAmountCents: amountCents,
        quantity: itemQuantity,
        isPaid: selectedPaymentState?.isPaid,
        createId: () => crypto.randomUUID(),
      });
    } catch (error) {
      showSnackbar(setSnackbar, error instanceof Error ? error.message : 'No se pudo agregar el concepto al carrito.');
      return;
    }
    setItems((current) => [...current, ...itemsToAdd]);
    resetChargeFields();
    setShowChargeSearch(false);
  };

  useEffect(() => {
    if (!initialShortcutParams.customerId && !initialShortcutParams.sourceType && !initialShortcutParams.sourceId && searchParams.get('add') !== '1') return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('customerId');
    nextParams.delete('sourceType');
    nextParams.delete('sourceId');
    nextParams.delete('add');
    setSearchParams(nextParams, { replace: true });
  }, []);

  useEffect(() => {
    if (searchParams.get('add') !== '1') return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('add');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const readdServices = (groupId: string) => {
    if (pageLoading || registerPayment.isPending) return;
    const serviceItems = serviceItemsForGroup(
      groupId,
      items,
      staysQuery.data?.items ?? [],
      daycaresQuery.data?.items ?? [],
      linkedStayQuery.data,
      linkedDaycareQuery.data,
      services,
      dogs,
      pricesByServiceId,
      priceLookups,
      paymentsQuery.data?.items ?? [],
      appointmentsQuery.data?.items ?? [],
    );

    setItems((current) => mergeServiceItemsForGroup(current, groupId, serviceItems));
    showSnackbar(setSnackbar, serviceItems.length > 0 ? 'Servicios asociados revisados.' : 'No hay servicios asociados para este perro.');
  };

  const pageError = servicesQuery.error ?? staysQuery.error ?? daycaresQuery.error ?? membershipsQuery.error ?? linkedStayQuery.error ?? linkedDaycareQuery.error ?? linkedServiceQuery.error ?? dogsQuery.error ?? paymentsQuery.error ?? pricesQuery.error ?? appointmentsQuery.error;
  const pageLoading = servicesQuery.isLoading || staysQuery.isLoading || daycaresQuery.isLoading || membershipsQuery.isLoading || linkedStayQuery.isLoading || linkedDaycareQuery.isLoading || linkedServiceQuery.isLoading || dogsQuery.isLoading || paymentsQuery.isLoading || pricesQuery.isLoading || appointmentsQuery.isLoading;
  const pageBusy = pageLoading || registerPayment.isPending;

  const paymentCanSubmit = canSubmitPayment({
    customerId: paymentCustomerId,
    itemCount: items.length,
    isPending: pageBusy,
    paymentCents: paymentAmountCents,
    remainingCents: remainingCartCents,
  });

  if (pageError) return <ErrorState message={pageError.message} />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">Pagos</h1>
          <p className="mt-1 text-sm text-slate-500">Selecciona guarderías, hotel o servicios y registra el cobro del cliente.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant={showChargeSearch ? 'secondary' : 'primary'} disabled={registerPayment.isPending} onClick={() => setShowChargeSearch((current) => !current)}>
            {showChargeSearch ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Search className="h-4 w-4" aria-hidden="true" />}
            {showChargeSearch ? 'Ocultar busqueda' : 'Buscar cobro'}
          </Button>
          <Link className="inline-flex h-9 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/payments/history">
            <History className="h-4 w-4" aria-hidden="true" />
            Historial
          </Link>
        </div>
      </div>
      {pageLoading || registerPayment.isPending ? (
        <div className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3 text-sm font-medium text-[#4f5d49]" role="status">
          <span className="inline-flex items-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#d6ded0] border-t-[#334033]" aria-hidden="true" />
            {registerPayment.isPending ? 'Registrando pago...' : 'Cargando informacion de cobro...'}
          </span>
        </div>
      ) : null}
      <section className="grid gap-5">
        {showChargeSearch ? <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-4" role="dialog" aria-modal="true">
          <Card className="max-h-[90vh] w-full max-w-3xl overflow-y-auto">
          <CardHeader><h2 className="text-sm font-semibold">Nuevo cobro</h2></CardHeader>
          <CardContent className="grid gap-3">
            {pageLoading ? <PaymentLoadingPanel label="Cargando conceptos, precios y pagos..." /> : null}
            <div className="grid gap-1.5">
              <Label htmlFor="customerId">Cliente</Label>
              <CustomerSearchBox id="customerId" value={customerId} disabled={pageBusy} placeholder="Selecciona un cliente" onChange={(nextCustomerId) => {
                setCustomerId(nextCustomerId);
                setSourceType('');
                setSourceId('');
                setManualAmount('0');
                setQuantity('1');
              }} />
            </div>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="grid min-w-0 gap-1.5">
                <Label htmlFor="sourceType">Tipo</Label>
                <Select id="sourceType" value={sourceType} disabled={!customerId || pageLoading} onChange={(event) => { setSourceType(event.target.value as SourceType | ''); setSourceId(''); setDescription(''); setManualAmount('0'); setQuantity('1'); }}>
                  <option value="">Selecciona un tipo</option>
                  <option value="daycare">Guardería</option>
                  <option value="stay">Hotel</option>
                  <option value="service">Servicio</option>
                  <option value="membership">Membresia</option>
                  <option value="other">Otro</option>
                </Select>
              </div>
              <div className="grid min-w-0 gap-1.5">
                <Label htmlFor="sourceId">Concepto</Label>
                <Select id="sourceId" value={sourceId} disabled={!customerId || !sourceType || sourceType === 'other' || pageLoading} onChange={(event) => changeSource(event.target.value)}>
                  <option value="">{sourceType === 'other' ? 'Cobro personalizado' : !customerId ? 'Selecciona un cliente primero' : !sourceType ? 'Selecciona un tipo primero' : 'Selecciona'}</option>
                  {sourceOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </Select>
                {sourceLookupsEnabled && pageLoading ? <p className="text-xs text-[#687461]">Cargando conceptos disponibles...</p> : null}
              </div>
            </div>
            {!autoFilledSelection ? <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="quantity">Cantidad</Label>
                <Input id="quantity" className={autoFilledSelection ? 'bg-[#eef3ea] text-[#687461] opacity-80' : undefined} type="number" min="1" step="1" value={quantity} disabled={autoFilledSelection || pageLoading} readOnly={autoFilledSelection} onChange={(event) => setQuantity(event.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="amount">Monto unitario MXN</Label>
                <Input id="amount" className={autoFilledSelection ? 'bg-[#eef3ea] text-[#687461] opacity-80' : undefined} type="number" min="0.01" step="0.01" value={manualAmount} disabled={autoFilledSelection || pageLoading} readOnly={autoFilledSelection} onChange={(event) => setManualAmount(event.target.value)} />
              </div>
            </div> : null}
            {sourceType === 'other' || selectedSource ? <div className="grid gap-1.5">
              <Label htmlFor="chargeDescription">Descripción</Label>
              <Textarea id="chargeDescription" maxLength={1000} readOnly={sourceType !== 'other'}
                value={sourceType === 'other' ? description : selectedSuggestedItems.filter((item) => item.itemKind !== 'dog' && item.itemKind !== 'main').map((item) => item.description ?? defaultPaymentDescription(item.label)).join('\n') || (selectedSource ? ('description' in selectedSource.source && selectedSource.source.description?.trim() || defaultPaymentDescription(selectedSource.label)) : '')}
                onChange={(event) => setDescription(event.target.value)} placeholder="Descripción opcional del cobro" />
              {sourceType === 'other' ? <p className="text-sm font-medium">Subtotal: {formatCurrency(centsFromAmountInput(manualAmount) * quantityFromInput(quantity))}</p> : null}
            </div> : null}
            {selectedSource ? (
              <SuggestedConceptList items={selectedSuggestedItems} />
            ) : null}
            {selectedSource ? <SelectedSourceSummary source={selectedSource} suggestedItems={selectedSuggestedItems} payments={paymentsQuery.data?.items ?? []} dogs={dogs} /> : null}
            {selectedPaymentState?.isPaid ? (
              <div className="rounded-md border border-[#b9d8b5] bg-[#e5f3e4] p-3 text-sm font-medium text-[#2f6b38]" role="status">
                Este concepto ya esta pagado. El flujo de cobro queda bloqueado para evitar duplicar pagos.
              </div>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="secondary" disabled={registerPayment.isPending} onClick={closeChargeSearch}>Cancelar</Button>
              <Button type="button" disabled={(!selectedSource && sourceType !== 'other') || !customerId || pageBusy || Boolean(selectedPaymentState?.isPaid)} onClick={addItem}><Plus className="h-4 w-4" aria-hidden="true" />Agregar al carrito</Button>
            </div>
          </CardContent>
        </Card>
        </div> : null}
        <div className="flex items-center gap-3" role="separator" aria-label="Carrito de cobro">
          <div className="h-px flex-1 bg-[#d6ded0]" />
          <span className="rounded-md border border-[#d6ded0] bg-[#eef3ea] px-3 py-1 text-xs font-semibold uppercase text-[#4f5d49]">
            Carrito de cobro
          </span>
          <div className="h-px flex-1 bg-[#d6ded0]" />
        </div>
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold">Carrito</h2>
                <p className="mt-1 flex items-center gap-2 text-xs text-[#687461]">
                  <TimerReset className="h-4 w-4" aria-hidden="true" />
                  {items.length > 0 ? `Recordado por ${formatDuration(cartTimeLeftMs)}` : 'Sin conceptos recordados'}
                </p>
              </div>
              <Button type="button" variant="secondary" disabled={items.length === 0 || pageBusy} onClick={clearCart}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Limpiar carrito
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pageBusy ? <PaymentLoadingPanel label={registerPayment.isPending ? 'Registrando pago...' : 'Actualizando saldos y conceptos...'} /> : null}
            <div className="overflow-x-auto">
              <Table>
                <thead><tr><Th>Concepto</Th><Th>Cantidad</Th><Th>Monto</Th><Th>Subtotal</Th><Th>Acciones</Th></tr></thead>
                <tbody>
                  {cartGroups.map((group) => (
                    <GroupedCartRows
                      key={group.id}
                      group={group}
                      disabled={pageBusy}
                      onQuantityChange={(itemId, nextQuantity) => setItems((current) => current.map((candidate) => candidate.id === itemId ? { ...candidate, quantity: candidate.sourceType === 'other' ? Math.floor(quantityFromInput(nextQuantity)) : quantityFromInput(nextQuantity) } : candidate))}
                      onRemoveGroup={(groupId) => setItems((current) => current.filter((candidate) => candidate.groupId !== groupId))}
                      onRemoveItem={(item) => setItems((current) => current.filter((candidate) => item.itemKind === 'main' ? candidate.parentId !== item.parentId : candidate.id !== item.id))}
                      onReaddServices={readdServices}
                    />
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <div className="grid gap-1.5">
                <Label htmlFor="method">Método</Label>
                <Select id="method" value={method} disabled={pageBusy} onChange={(event) => setMethod(event.target.value as Payment['method'])}>
                  <option value="cash">Efectivo</option>
                  <option value="card">Tarjeta</option>
                  <option value="transfer">Transferencia</option>
                  <option value="other">Otro</option>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="notes">Notas</Label>
                <Textarea id="notes" className="min-h-9" value={notes} disabled={pageBusy} onChange={(event) => setNotes(event.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="paymentAmount">A pagar ahora MXN</Label>
                <Input id="paymentAmount" type="number" min="0" step="1" value={paymentAmount} disabled={pageBusy} onChange={(event) => setPaymentAmount(event.target.value)} />
                <p className="text-xs text-[#687461]">Pendiente: {formatCurrency(remainingCartCents)}</p>
              </div>
              <div className="flex flex-col items-end justify-end gap-2">
                <div className="text-right text-sm text-[#687461]">Pagado antes: {formatCurrency(paidInCartCents)}</div>
                <div className="text-lg font-semibold">Total: {formatCurrency(totalCents)}</div>
                <Button type="button" disabled={!paymentCanSubmit} onClick={() => registerPayment.mutate()}>
                  <CreditCard className="h-4 w-4" aria-hidden="true" />
                  Registrar pago
                </Button>
              </div>
            </div>
            {paymentAmountCents > remainingCartCents ? <p className="mt-3 text-sm text-rose-600">El monto a pagar no puede superar el pendiente.</p> : null}
            {registerPayment.isError ? <p className="mt-3 text-sm text-rose-600">{registerPayment.error.message}</p> : null}
          </CardContent>
        </Card>
      </section>
      {snackbar ? (
        <div className="fixed bottom-5 right-5 z-50 max-w-sm rounded-md border border-[#d6ded0] bg-white px-4 py-3 text-sm font-medium text-[#334033] shadow-lg" role="status" aria-live="polite">
          {snackbar}
        </div>
      ) : null}
    </div>
  );
}

function GroupedCartRows({
  group,
  disabled,
  onQuantityChange,
  onRemoveGroup,
  onRemoveItem,
  onReaddServices,
}: {
  readonly group: CartGroup;
  readonly disabled?: boolean;
  readonly onQuantityChange: (itemId: string, quantity: number) => void;
  readonly onRemoveGroup: (groupId: string) => void;
  readonly onRemoveItem: (item: CartItem) => void;
  readonly onReaddServices: (groupId: string) => void;
}) {
  return (
    <>
      <tr>
        <Td colSpan={5} className="bg-[#eef3ea] text-xs font-semibold uppercase text-[#334033]">
          <div className="flex items-center justify-between gap-3">
            <span>{group.label}</span>
            <div className="flex items-center gap-2">
              <Button variant="secondary" className="h-7 px-2 text-xs normal-case" disabled={disabled} onClick={() => onReaddServices(group.id)}>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Reagregar servicios
              </Button>
              <Button variant="danger" className="h-7 px-2 text-xs normal-case" disabled={disabled} onClick={() => onRemoveGroup(group.id)}>
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                Quitar
              </Button>
            </div>
          </div>
        </Td>
      </tr>
      {group.items.map((item) => {
        const isMain = item.itemKind === 'main';
        const isDog = item.itemKind === 'dog';
        const isExtra = item.itemKind === 'extra';
        const isLinkedService = item.itemKind === 'service' && (item.sourceType === 'stay' || item.sourceType === 'daycare');
        if (isMain) return null;
        if (isDog) {
          return (
            <tr key={item.id} className="bg-[#f8faf6]">
              <Td colSpan={5} className="pl-8 text-sm font-semibold text-[#334033]">
                {item.label}
              </Td>
            </tr>
          );
        }
        return (
          <tr key={item.id} className={isExtra ? 'bg-[#f3e5df]' : isMain ? 'bg-white' : 'bg-[#fbfcfa]'}>
            <Td className={isExtra ? 'font-semibold text-[#6f483d]' : isMain ? 'font-semibold text-slate-950' : 'pl-12 text-[#4f5d49]'}>
              {isMain ? item.label : <span className={isExtra ? 'inline-flex items-center gap-2 rounded-md border border-[#d8b9ad] bg-white px-2.5 py-1.5 shadow-sm ring-1 ring-[#f3e5df]' : 'border-l-2 border-[#d6ded0] pl-3'}>{item.label}{item.paidCents ? <span className="ml-2 rounded-md bg-[#e5f3e4] px-2 py-0.5 text-[10px] font-semibold text-[#2f6b38]">Pagado {formatCurrency(item.paidCents)}</span> : null}</span>}
              <p className="mt-1 text-xs text-[#687461]">{item.description ?? (item.sourceType === 'other' ? '' : defaultPaymentDescription(item.label))}</p>
            </Td>
            <Td>
              <div className="inline-flex h-9 items-center overflow-hidden rounded-md border border-[#d6ded0] bg-white shadow-sm">
                <button className="flex h-9 w-8 items-center justify-center text-[#4f5d49] hover:bg-[#eef3ea] disabled:cursor-not-allowed disabled:opacity-45" type="button" aria-label="Reducir cantidad" disabled={disabled} onClick={() => onQuantityChange(item.id, Math.max(1, item.quantity - 1))}>
                  <Minus className="h-4 w-4" aria-hidden="true" />
                </button>
                <Input className="h-9 w-14 rounded-none border-0 text-center shadow-none" type="number" min="1" value={item.quantity} disabled={disabled} onChange={(event) => onQuantityChange(item.id, quantityFromInput(event.target.value))} />
                <button className="flex h-9 w-8 items-center justify-center text-[#4f5d49] hover:bg-[#eef3ea] disabled:cursor-not-allowed disabled:opacity-45" type="button" aria-label="Aumentar cantidad" disabled={disabled} onClick={() => onQuantityChange(item.id, item.quantity + 1)}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </Td>
            <Td><span className="inline-flex min-w-24 justify-end rounded-md border border-[#d6ded0] bg-[#eef3ea] px-2.5 py-1 text-sm font-medium text-[#687461] opacity-80" aria-readonly="true">{formatCurrency(item.amountCents)}</span></Td>
            <Td className={isMain || isExtra ? 'font-semibold text-slate-900' : undefined}>{formatCurrency(item.amountCents * item.quantity)}</Td>
            <Td>
              <Button
                variant="danger"
                className="h-8 w-8 px-0 disabled:cursor-not-allowed disabled:opacity-45"
                disabled={disabled || isLinkedService}
                title={disabled ? 'Espera a que termine la carga.' : isLinkedService ? 'Quita este servicio desde hotel o guardería.' : 'Quitar concepto'}
                aria-label={disabled ? 'Accion deshabilitada mientras carga' : isLinkedService ? 'Servicio asociado, quítalo desde hotel o guardería' : 'Quitar concepto'}
                onClick={() => onRemoveItem(item)}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </Td>
          </tr>
        );
      })}
    </>
  );
}

function PaymentLoadingPanel({ label }: { readonly label: string }) {
  return (
    <div className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3" role="status" aria-live="polite">
      <div className="flex items-center gap-2 text-sm font-medium text-[#4f5d49]">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#d6ded0] border-t-[#334033]" aria-hidden="true" />
        {label}
      </div>
      <div className="mt-3 grid gap-2" aria-hidden="true">
        <div className="h-2.5 w-3/4 animate-pulse rounded-sm bg-[#dfe8d8]" />
        <div className="h-2.5 w-1/2 animate-pulse rounded-sm bg-[#eef3ea]" />
      </div>
    </div>
  );
}

function SuggestedConceptList({ items }: { readonly items: SuggestedItem[] }) {
  if (items.length === 0) {
    return (
      <div className="rounded-md border border-[#d6ded0] bg-[#f8faf6] p-3 text-sm text-[#4f5d49]">
        Escribe el monto a cobrar para este concepto.
      </div>
    );
  }
  const subtotalCents = totalForSuggestedItems(items);
  const paidCents = paidCentsForSuggestedItems(items);
  const remainingCents = Math.max(0, subtotalCents - paidCents);

  return (
    <div className="rounded-md border border-[#d6ded0] bg-[#f8faf6] p-3">
      <p className="text-sm font-semibold text-[#334033]">Conceptos detectados</p>
      <ul className="mt-2 grid gap-2">
        {items.map((item) => (
          <li key={`${item.parentId}-${item.itemKind}-${item.dogId ?? ''}-${item.label}`} className={`flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-3 py-2 text-sm ${item.itemKind === 'main' ? 'font-semibold text-[#223126]' : item.itemKind === 'dog' ? 'ml-4 font-semibold text-[#334033]' : 'ml-8 text-[#4f5d49]'}`}>
            <span>{item.label}{item.paidCents ? <span className="ml-2 rounded-md bg-[#e5f3e4] px-2 py-0.5 text-[10px] font-semibold text-[#2f6b38]">Pagado {formatCurrency(item.paidCents)}</span> : null}</span>
            <span className="font-medium text-[#334033]">{item.itemKind === 'main' || item.itemKind === 'dog' ? formatCurrency(0) : `${item.quantity} x ${formatCurrency(item.amountCents)}`}</span>
          </li>
        ))}
      </ul>
      <div className="mt-3 grid gap-2 rounded-md border border-[#d6ded0] bg-white p-3 text-sm sm:grid-cols-3">
        <div>
          <p className="text-xs text-[#687461]">Subtotal</p>
          <p className="font-semibold text-[#223126]">{formatCurrency(subtotalCents)}</p>
        </div>
        <div>
          <p className="text-xs text-[#687461]">Ya pagado</p>
          <p className="font-semibold text-[#223126]">{formatCurrency(paidCents)}</p>
        </div>
        <div>
          <p className="text-xs text-[#687461]">A pagar</p>
          <p className="font-semibold text-[#223126]">{formatCurrency(remainingCents)}</p>
        </div>
      </div>
    </div>
  );
}

function SelectedSourceSummary({
  source,
  suggestedItems,
  payments,
  dogs,
}: {
  readonly source: SourceOption;
  readonly suggestedItems: SuggestedItem[];
  readonly payments: Payment[];
  readonly dogs: Dog[];
}) {
  const { dueCents, paidCents, remainingCents } = paymentStateForSource(source, suggestedItems, payments);
  const status = paymentStatus(dueCents, paidCents);

  return (
    <div className="grid gap-2 rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-[#334033]">Estado del cobro</p>
          <p className="text-xs text-[#687461]">{source.label}</p>
        </div>
        <span className={`rounded-md px-2.5 py-1 text-xs font-semibold ${paymentToneClass(status.tone)}`}>{status.label}</span>
      </div>
      <div className="grid gap-2 text-sm sm:grid-cols-3">
        <div className="rounded-md bg-white p-2">
          <p className="text-xs text-[#687461]">Total</p>
          <p className="font-semibold text-[#223126]">{formatCurrency(dueCents)}</p>
        </div>
        <div className="rounded-md bg-white p-2">
          <p className="text-xs text-[#687461]">Pagado</p>
          <p className="font-semibold text-[#223126]">{formatCurrency(paidCents)}</p>
        </div>
        <div className="rounded-md bg-white p-2">
          <p className="text-xs text-[#687461]">Restante</p>
          <p className="font-semibold text-[#223126]">{formatCurrency(remainingCents)}</p>
        </div>
      </div>
      <ExtraTimeWarning source={source.source} dogs={dogs} />
    </div>
  );
}

function ExtraTimeWarning({ source, dogs }: { readonly source: Appointment | Daycare | MembershipPurchase | Service | Stay; readonly dogs: Dog[] }) {
  if (!('dogId' in source)) return null;
  const dogNames = dogIdsForSource(source).map((dogId) => dogs.find((dog) => dog.id === dogId)?.name ?? dogId).join(', ');
  const extra = 'checkOutDate' in source
    ? extraStayDays(source)
    : extraDaycareHours(source);
  if (extra <= 0) return null;
  const unit = 'checkOutDate' in source ? 'día(s)' : 'hora(s)';
  return (
    <div className="rounded-md border border-[#e6c56f] bg-[#fff8df] p-3 text-sm font-medium text-[#735817]" role="status">
      Atención: {dogNames} tiene(n) {extra} {unit} extra. Agrégalo manualmente al cobro si aplica.
    </div>
  );
}

function paymentToneClass(tone: 'paid' | 'partial' | 'pending') {
  if (tone === 'paid') return 'bg-[#e5f3e4] text-[#2f6b38]';
  if (tone === 'partial') return 'bg-[#fff3d6] text-[#7a5a16]';
  return 'bg-[#f3e5df] text-[#8a4a3a]';
}

function showSnackbar(setSnackbar: (message: string) => void, message: string) {
  setSnackbar(message);
  window.setTimeout(() => setSnackbar(''), 3000);
}

function readStoredCart(): StoredCart {
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return { items: [], expiresAt: 0 };
    const parsed = JSON.parse(raw) as Partial<StoredCart>;
    if (!Array.isArray(parsed.items) || typeof parsed.expiresAt !== 'number') {
      window.localStorage.removeItem(CART_STORAGE_KEY);
      return { items: [], expiresAt: 0 };
    }
    if (parsed.expiresAt <= Date.now()) {
      window.localStorage.removeItem(CART_STORAGE_KEY);
      return { items: [], expiresAt: 0 };
    }
    return { items: parsed.items.filter(isStoredCartItem), expiresAt: parsed.expiresAt };
  } catch {
    window.localStorage.removeItem(CART_STORAGE_KEY);
    return { items: [], expiresAt: 0 };
  }
}

function writeStoredCart(items: CartItem[], expiresAt: number) {
  window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ items, expiresAt }));
}

function formatDuration(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

